/**
 * THRM-04, solved instead of assumed.
 *
 * The closed-form furnace widens a Gaussian: sigma grows as sqrt(2Dt), the
 * profile keeps its shape, and the junction is wherever that Gaussian crosses
 * the substrate doping. It is exactly right for one case - a small amount of
 * dopant, in an infinite solid, with a diffusivity that does not depend on
 * anything. A real source/drain anneal is none of those.
 *
 * WHAT THE CLOSED FORM CANNOT REPRESENT, AND WHY EACH ONE MATTERS
 *
 *   D DEPENDS ON CONCENTRATION.  Diffusion in silicon is mediated by charged
 *   point defects, and their population depends on where the Fermi level is,
 *   which depends on the doping. So D is not a constant: it is two to five
 *   times larger in the peak of a source/drain implant than in the tail. The
 *   profile therefore does not keep its shape - the top moves faster than the
 *   bottom and the profile squares off into the box shape a real SIMS trace
 *   shows. A Gaussian can be wide or narrow. It cannot be square.
 *
 *   THE SURFACE MOVES.  If the anneal is oxidising, silicon is being consumed
 *   the whole time. The boundary the diffusion equation is solved against is
 *   travelling into the dopant, and the dopant is being partitioned across it
 *   according to a segregation coefficient. Boron is taken up by the oxide and
 *   depletes at the surface; phosphorus is rejected and piles up. Those are
 *   opposite signs of the same effect, they are large, and they set the surface
 *   concentration a contact will later be made to.
 *
 *   THERE IS A CEILING.  Only so much dopant will sit on a lattice site at a
 *   given temperature. Above the solid solubility the excess is electrically
 *   dead - it is still there, SIMS still sees it, and it carries no current.
 *   So the electrical junction and the chemical junction are different depths,
 *   and sheet resistance is an integral over only the active part.
 *
 * WHAT IS SOLVED AND WHAT IS NOT
 *
 *   SOLVED   the diffusion equation itself, on a grid, with D varying point by
 *            point, a moving boundary, and flux-conserving segregation across
 *            it. Junction depth, surface concentration, sheet resistance and
 *            profile shape all come out of that one calculation.
 *
 *   TAKEN    the diffusivity prefactors and activation energies, which are
 *            measured constants and are the same ones the closed form uses.
 *            Nothing here is fitted to make an answer come out.
 */

import { clamp } from '../../core/math'
import { growOxide, type Ambient, type Dopant } from './ThermalSim'

/** Intrinsic carrier concentration in silicon [cm^-3] at temperature T [C]. */
export function intrinsicCarriers(tempC: number): number {
  const t = tempC + 273.15
  return 3.1e16 * Math.pow(t, 1.5) * Math.exp(-0.603 / (8.617e-5 * t))
}

/**
 * Diffusivity constants.
 *
 * The neutral term is the one the closed form uses. The charged terms are the
 * ones it leaves out: a fraction of the diffusion is mediated by singly and
 * doubly charged vacancies whose population scales with the carrier
 * concentration, so the more dopant there is the faster it moves.
 */
/**
 * Volume expansion when silicon becomes oxide.
 *
 * A micron of silicon makes 2.27 microns of oxide. It is a density ratio, it
 * is why 44% of a grown oxide came out of the wafer, and it is the reason the
 * segregation balance has a factor in it larger than one.
 */
const OXIDE_EXPANSION = 2.27

const DIFF = {
  boron: { d0: 0.037, ea: 3.46, dPlus0: 0.76, eaPlus: 3.46, charge: +1 },
  phosphorus: { d0: 3.85, ea: 3.66, dPlus0: 4.44, eaPlus: 4.0, charge: -1 },
  arsenic: { d0: 0.066, ea: 3.44, dPlus0: 12.0, eaPlus: 4.05, charge: -1 },
} as const

/** Solid solubility [cm^-3] - the ceiling on how much will sit on a site. */
export function solidSolubility(dopant: Dopant, tempC: number): number {
  const t = tempC + 273.15
  const p = {
    boron: { c0: 2.4e22, ea: 0.62 },
    phosphorus: { c0: 3.1e22, ea: 0.61 },
    arsenic: { c0: 1.9e22, ea: 0.47 },
  }[dopant]
  return p.c0 * Math.exp(-p.ea / (8.617e-5 * t))
}

/**
 * Segregation coefficient at the silicon-oxide interface.
 *
 * The ratio of the concentration on the silicon side to the oxide side at
 * equilibrium. Below one the oxide takes dopant up and the silicon surface
 * depletes; above one the oxide rejects it and the surface piles up. Boron and
 * phosphorus sit on opposite sides of that line, which is why the same anneal
 * makes one contact easier and the other harder.
 */
export const SEGREGATION: Record<Dopant, number> = {
  boron: 0.3,
  phosphorus: 10,
  arsenic: 10,
}

/**
 * Concentration-dependent diffusivity [cm^2/s].
 *
 * D = D_neutral + D_charged * (n/ni)^|z|, the standard charged-defect model.
 * At low concentration the second term vanishes and this reduces exactly to the
 * constant the closed form uses - which is the point: the solver contains the
 * formula as its own low-concentration limit.
 */
export function diffusivityAt(dopant: Dopant, tempC: number, concentration: number): number {
  const d = DIFF[dopant]
  const kT = 8.617e-5 * (tempC + 273.15)
  const neutral = d.d0 * Math.exp(-d.ea / kT)
  const charged = d.dPlus0 * Math.exp(-d.eaPlus / kT)
  const ni = intrinsicCarriers(tempC)
  // Carrier concentration from charge neutrality, capped at what is active.
  const active = Math.min(concentration, solidSolubility(dopant, tempC))
  const n = 0.5 * (active + Math.sqrt(active * active + 4 * ni * ni))
  return neutral + charged * (n / ni)
}

/**
 * An anneal ambient, including the one the oxidation model does not have.
 *
 * ThermalSim's Ambient is 'dry' or 'wet' because Deal-Grove is a model of
 * oxidation and an inert anneal does not oxidise. Diffusion happens in all
 * three, and whether the surface is moving underneath it is exactly the
 * difference between a spike anneal and a drive-in.
 */
export type AnnealAmbient = Ambient | 'inert'

export interface AnnealStep {
  label: string
  tempC: number
  seconds: number
  ambient: AnnealAmbient
}

export interface DiffusionOptions {
  dopant: Dopant
  /** Implanted dose [cm^-2]. */
  dose: number
  /** As-implanted range and straggle [nm] - the profile this starts from. */
  rangeNm: number
  straggleNm: number
  /** Substrate background doping [cm^-3]. */
  substrate: number
  /** Grid depth [nm] and node count. */
  depthNm?: number
  nodes?: number
}

export interface DiffusionResult {
  depthNm: Float64Array
  /** Chemical concentration [cm^-3] - what SIMS measures. */
  concentration: Float64Array
  /** Electrically active concentration - what carries current. */
  active: Float64Array
  /** Junction where the ACTIVE profile crosses the substrate [nm]. */
  junctionNm: number
  /** Junction the chemical profile would give. Deeper, and it is not the junction. */
  chemicalJunctionNm: number
  /** Concentration at the surface [cm^-3]. */
  surfaceCm3: number
  /** Sheet resistance of the active layer [ohm/square]. */
  sheetResistance: number
  /** Fraction of the implanted dose still electrically active. */
  activeFraction: number
  /**
   * Fraction of the implanted dose still in the silicon.
   *
   * Lost two ways, and they are different: taken up by the oxide across the
   * interface, and simply consumed as the interface swept past it. Both only
   * happen while an oxide is growing.
   */
  doseRetainedFraction: number
  /** Silicon consumed by oxidation [nm]. */
  siliconConsumedNm: number
  /** Ratio of the largest diffusivity on the grid to the smallest. */
  diffusivityRatio: number
  /** Time steps taken. */
  steps: number
}

/** Mobility [cm^2/Vs] against active doping, Caughey-Thomas. */
function mobility(dopant: Dopant, n: number): number {
  const m =
    dopant === 'boron'
      ? { min: 44.9, max: 470.5, ref: 2.23e17, alpha: 0.719 }
      : { min: 68.5, max: 1414, ref: 9.2e16, alpha: 0.711 }
  return m.min + (m.max - m.min) / (1 + Math.pow(Math.max(n, 1) / m.ref, m.alpha))
}

/**
 * Solve the diffusion equation through a sequence of anneal steps.
 *
 * Explicit in time with a stability-limited step. Explicit is the right choice
 * here and not a shortcut: the whole demonstration is that D varies across the
 * grid, and an explicit scheme makes the stability limit - which is set by the
 * LARGEST D anywhere - visible rather than hidden inside a matrix solve. When
 * the peak diffuses five times faster than the tail, the time step the whole
 * grid must take is set by the peak, and that is a real cost of the physics
 * rather than an artefact of the method.
 */
/**
 * A grid deep enough to hold the profile, from a cheap estimate of how far the
 * dopant will actually move.
 */
function autoDepthNm(opts: DiffusionOptions, steps: AnnealStep[]): number {
  const peak = opts.dose / (Math.max(opts.straggleNm, 1) * 1e-7 * Math.sqrt(2 * Math.PI))
  let dt = 0
  for (const s of steps) dt += diffusivityAt(opts.dopant, s.tempC, peak) * s.seconds
  const spreadNm = Math.sqrt(2 * dt) * 1e7
  return Math.max(600, 2.5 * (opts.rangeNm + 6 * (opts.straggleNm + spreadNm)))
}

export function solveDiffusion(
  opts: DiffusionOptions,
  steps: AnnealStep[],
): DiffusionResult {
  const nodes = opts.nodes ?? 600
  // Size the grid to the answer rather than guessing it.
  //
  // A fixed 600 nm grid was the first draft, and a long drive-in ran straight
  // off the end of it - which reports a junction at exactly the grid depth,
  // and that is not a junction, it is a missing boundary. The estimate below
  // uses the ENHANCED diffusivity, because using the intrinsic one would size
  // the grid with the same assumption the solver exists to get rid of.
  const depthNm = opts.depthNm ?? autoDepthNm(opts, steps)
  const dx = depthNm / (nodes - 1)
  const dxCm = dx * 1e-7

  const x = new Float64Array(nodes)
  const c = new Float64Array(nodes)
  for (let i = 0; i < nodes; i++) {
    x[i] = i * dx
    // As-implanted Gaussian. This is the ONLY Gaussian in the model, and it is
    // the starting condition rather than the answer.
    const g =
      Math.exp(-0.5 * Math.pow((x[i] - opts.rangeNm) / Math.max(opts.straggleNm, 0.5), 2)) /
      (Math.max(opts.straggleNm, 0.5) * 1e-7 * Math.sqrt(2 * Math.PI))
    c[i] = opts.dose * g
  }

  let consumedNm = 0
  let segregatedDose = 0
  let totalSteps = 0
  let dMin = Infinity
  let dMax = 0

  for (const step of steps) {
    if (step.seconds <= 0) continue
    const seg = SEGREGATION[opts.dopant]

    // Silicon consumed by this step, if it is oxidising. Deal-Grove gives the
    // oxide; 44% of that thickness came out of the wafer.
    const oxideNm =
      step.ambient === 'inert'
        ? 0
        : growOxide(step.tempC, step.seconds / 60, step.ambient, 2) - 2
    const stepConsumedNm = 0.44 * oxideNm
    // How fast the interface is travelling into the wafer [cm/s].
    const vInterface = (stepConsumedNm * 1e-7) / Math.max(step.seconds, 1e-9)

    // Stability: the explicit scheme needs dt < dx^2 / (2 D) everywhere.
    let dPeak = 0
    for (let i = 0; i < nodes; i++) {
      dPeak = Math.max(dPeak, diffusivityAt(opts.dopant, step.tempC, c[i]))
    }
    const dtMax = (0.35 * dxCm * dxCm) / Math.max(dPeak, 1e-30)
    const n = Math.max(1, Math.ceil(step.seconds / dtMax))
    const dt = step.seconds / n
    totalSteps += n

    const next = new Float64Array(nodes)
    for (let s = 0; s < n; s++) {
      // Diffusivity at every node, from the concentration at that node.
      const d = new Float64Array(nodes)
      for (let i = 0; i < nodes; i++) {
        d[i] = diffusivityAt(opts.dopant, step.tempC, c[i])
        dMin = Math.min(dMin, d[i])
        dMax = Math.max(dMax, d[i])
      }
      // Interior: flux form, so the scheme conserves dopant even where D jumps.
      for (let i = 1; i < nodes - 1; i++) {
        const dRight = 0.5 * (d[i] + d[i + 1])
        const dLeft = 0.5 * (d[i] + d[i - 1])
        next[i] =
          c[i] + (dt / (dxCm * dxCm)) * (dRight * (c[i + 1] - c[i]) - dLeft * (c[i] - c[i - 1]))
      }
      // The surface, from a mass balance at the moving interface.
      //
      // In time dt the interface eats v.dt of silicon carrying C[0].v.dt of
      // dopant, and makes 2.27 times that thickness of oxide, which wants to
      // hold C[0]/m. Whether the oxide wants more dopant than the silicon just
      // handed it decides the sign, and 2.27/m is the whole of it: boron at
      // m = 0.3 wants seven times what arrives and strips the surface, while
      // phosphorus at m = 10 wants a fifth of it and the rest piles up.
      //
      // No fitted transfer coefficient. The rate is the oxidation rate, which is
      // why segregation stops the moment the oxide stops growing.
      const dSurf = d[0]
      const jOut = c[0] * vInterface * (OXIDE_EXPANSION / seg - 1)
      next[0] = Math.max(
        c[0] + (dt / dxCm) * ((dSurf * (c[1] - c[0])) / dxCm - jOut),
        0,
      )
      segregatedDose += jOut * dt
      next[nodes - 1] = next[nodes - 2]
      c.set(next)
    }
    consumedNm += stepConsumedNm
  }

  // The moving boundary: shift the grid up by the silicon that was eaten. What
  // was at the surface is now inside the oxide and no longer in the wafer.
  if (consumedNm > 0) {
    const shift = consumedNm / dx
    const shifted = new Float64Array(nodes)
    for (let i = 0; i < nodes; i++) {
      const src = i + shift
      const lo = Math.floor(src)
      const frac = src - lo
      shifted[i] =
        lo + 1 < nodes ? c[lo] * (1 - frac) + c[lo + 1] * frac : c[nodes - 1]
    }
    c.set(shifted)
  }

  // Electrical activation: whatever is above the solubility ceiling is present
  // and dead. The last step's temperature sets the ceiling, because that is
  // what the lattice last equilibrated to.
  const lastTemp = steps.length ? steps[steps.length - 1].tempC : 1000
  const ss = solidSolubility(opts.dopant, lastTemp)
  const active = new Float64Array(nodes)
  for (let i = 0; i < nodes; i++) active[i] = Math.min(c[i], ss)

  let junctionNm = 0
  let chemicalJunctionNm = 0
  for (let i = 0; i < nodes; i++) {
    if (active[i] > opts.substrate) junctionNm = x[i]
    if (c[i] > opts.substrate) chemicalJunctionNm = x[i]
  }

  // Sheet resistance: integrate conductivity over the active layer.
  let conductance = 0
  for (let i = 0; i < nodes && x[i] <= junctionNm; i++) {
    conductance += 1.602e-19 * active[i] * mobility(opts.dopant, active[i]) * dxCm
  }

  let dose = 0
  let activeDose = 0
  for (let i = 0; i < nodes; i++) {
    dose += c[i] * dxCm
    activeDose += active[i] * dxCm
  }

  return {
    depthNm: x,
    concentration: c,
    active,
    junctionNm,
    chemicalJunctionNm,
    surfaceCm3: c[0],
    sheetResistance: conductance > 0 ? 1 / conductance : Infinity,
    activeFraction: clamp(activeDose / Math.max(opts.dose, 1), 0, 1),
    doseRetainedFraction: clamp(dose / Math.max(opts.dose, 1), 0, 2),
    siliconConsumedNm: consumedNm,
    diffusivityRatio: dMin > 0 ? dMax / dMin : 1,
    steps: totalSteps,
  }
}

/**
 * The closed form, for comparison: a Gaussian widened by sqrt(2Dt).
 *
 * Written here rather than reused from ThermalSim so the two can be put on the
 * same axis with the same grid and the same units, and so the comparison is
 * against the formula itself rather than against a particular implementation
 * of it.
 */
export function closedFormProfile(
  opts: DiffusionOptions,
  steps: AnnealStep[],
  nodes = 600,
  depthNm = 1500,
): { depthNm: Float64Array; concentration: Float64Array; junctionNm: number; surfaceCm3: number } {
  const d = DIFF[opts.dopant]
  let dt = 0
  for (const s of steps) {
    // The constant-D assumption: evaluated at the intrinsic limit, which is
    // where the textbook formula lives.
    const kT = 8.617e-5 * (s.tempC + 273.15)
    dt += d.d0 * Math.exp(-d.ea / kT) * s.seconds
  }
  const sigmaCm = Math.sqrt(Math.pow(opts.straggleNm * 1e-7, 2) + 2 * dt)
  const sigmaNm = sigmaCm * 1e7
  const x = new Float64Array(nodes)
  const c = new Float64Array(nodes)
  const dx = depthNm / (nodes - 1)
  let junctionNm = 0
  for (let i = 0; i < nodes; i++) {
    x[i] = i * dx
    c[i] =
      (opts.dose / (sigmaCm * Math.sqrt(2 * Math.PI))) *
      Math.exp(-0.5 * Math.pow((x[i] - opts.rangeNm) / sigmaNm, 2))
    if (c[i] > opts.substrate) junctionNm = x[i]
  }
  return { depthNm: x, concentration: c, junctionNm, surfaceCm3: c[0] }
}
